import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../../../features/auth/hooks/useAuth';
import { submitBetaRequest } from '../../auth/services/authApi';
import { BETA_MODE } from '../../../shared/config/flags';
import PublicNavigation from '../../../shared/components/layout/PublicNavigation';
import Footer from '../../../shared/components/layout/Footer';
import SuccessMessage from '../../../shared/components/ui/SuccessMessage';
import AlertMessage from '../../../shared/components/ui/AlertMessage';
import { trackEvent } from '../../../shared/lib/analytics';

const betaRequestSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  investment_platforms: z.string().optional(),
  referral_source: z.string().optional(),
});

type BetaRequestFormData = z.infer<typeof betaRequestSchema>;

const BetaRequestPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect authenticated users to home page (landing page with "Get rich together")
  useEffect(() => {
    if (user) {
      navigate('/', { replace: true });
    }
  }, [user, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<BetaRequestFormData>({
    resolver: zodResolver(betaRequestSchema),
  });

  const onSubmit = async (data: BetaRequestFormData) => {
    try {
      setError(null);
      setIsSubmitting(true);

      const trimmedEmail = data.email.toLowerCase().trim();
      await submitBetaRequest({
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        email: trimmedEmail,
        investment_platforms: data.investment_platforms?.trim() || undefined,
        referral_source: data.referral_source?.trim() || undefined,
      });

      // Track successful beta request submission
      // Only send email domain (not full email) to avoid PII
      const emailDomain = trimmedEmail.split('@')[1] || 'unknown';
      trackEvent('beta_request_submitted', {
        email_domain: emailDomain,
        source: 'marketing_site',
      });

      setIsSubmitted(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <PublicNavigation />
        <div className="flex-grow flex items-center justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <SuccessMessage
                message="Your beta access request has been submitted. We'll review it and get back to you soon!"
                linkText="Go back to homepage"
                linkTo="/"
              />
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto">
          {/* Logo and heading section - full width on all screens */}
          <div className="w-full mb-8">
            <div className="flex justify-center mb-6">
              <Link to="/">
                <img
                  src="/Vester-logo-lrg-clr.png"
                  alt="Vester"
                  className="h-20 sm:h-24 w-auto"
                />
              </Link>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Request Beta Access
            </h2>
            {!BETA_MODE && (
              <p className="mt-2 text-center text-sm text-gray-600">
                Already have access?{' '}
                <Link to="/signup" className="font-medium text-green-600 hover:text-green-500">
                  Sign up
                </Link>
                {' '}or{' '}
                <Link to="/login" className="font-medium text-green-600 hover:text-green-500">
                  log in
                </Link>
              </p>
            )}
          </div>

          {/* Form section - full width on all screens */}
          <div className="w-full">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
            {error && (
              <div className="mb-4">
                <AlertMessage
                  type="error"
                  message={error}
                  onClose={() => setError(null)}
                />
              </div>
            )}

            <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
              <div>
                <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                  First name
                </label>
                <div className="mt-1">
                  <input
                    id="first_name"
                    type="text"
                    autoComplete="given-name"
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.first_name ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                    {...register('first_name')}
                  />
                  {errors.first_name && (
                    <p className="mt-2 text-sm text-red-600">{errors.first_name.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                  Last name
                </label>
                <div className="mt-1">
                  <input
                    id="last_name"
                    type="text"
                    autoComplete="family-name"
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.last_name ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                    {...register('last_name')}
                  />
                  {errors.last_name && (
                    <p className="mt-2 text-sm text-red-600">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    type="email"
                    autoComplete="email"
                    className={`appearance-none block w-full px-3 py-2 border ${
                      errors.email ? 'border-red-300' : 'border-gray-300'
                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm`}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="investment_platforms" className="block text-sm font-medium text-gray-700">
                  Which investment platforms do you use? <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <div className="mt-1">
                  <input
                    id="investment_platforms"
                    type="text"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    placeholder="E*Trade, Coinbase, Vanguard, etc."
                    {...register('investment_platforms')}
                  />
                </div>
              </div>

              <div>
                <label htmlFor="referral_source" className="block text-sm font-medium text-gray-700">
                  How'd you hear about Vester? <span className="text-gray-500 font-normal">(optional)</span>
                </label>
                <div className="mt-1">
                  <input
                    id="referral_source"
                    type="text"
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    placeholder="Alex J, social media, search engine, etc."
                    {...register('referral_source')}
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Submitting...' : 'Submit Request'}
                </button>
              </div>
            </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default BetaRequestPage;

